/**
 * A clip's project: everything subty keeps about one video, and the only thing
 * the server stores.
 *
 * The video itself never leaves the operator's machine. A project is found
 * again by the file's fingerprint -- its size and a hash of its first and last
 * mebibyte -- so renaming or moving the file loses nothing, and a different
 * file never inherits another's corrections.
 *
 * The page and the server both hold a project to `parseProject`: the page on
 * every load, the server on every write. A document it refuses is never stored.
 */

import { PLACEMENTS, type Placement } from "./cover";
import { CAPTION_DESIGNS, CAPTION_DESIGN_NAMES, type CaptionDesignName, DEFAULT_FONT, FONTS, POSITIONS, STYLES } from "./presets";
import { repr } from "./py";

export const PROJECT_VERSION = 1;

/** `<size>-<sha256 hex>`: what a project is stored and fetched under. */
export const FINGERPRINT = /^(0|[1-9][0-9]{0,15})-[0-9a-f]{64}$/;

/** The most a project document may weigh: an hour of speech is about half a megabyte of timings. */
export const MAX_PROJECT_BYTES = 4 << 20;

export const MAX_NAME = 255;
export const MAX_WORDS = 50_000;
export const MAX_WORD = 200;
export const MAX_TRANSCRIPT = 500_000;
export const MAX_HEADLINE = 300;

/** One recognised word as measured, in milliseconds; sanitized on use, never on store. */
export interface Timing {
  readonly text: string;
  readonly start: number;
  readonly end: number;
}

/** The operator's choices for this clip. */
export interface Choices {
  /** Null for an existing custom design; absent in older saved documents. */
  readonly captionDesign: CaptionDesignName | null;
  readonly style: string;
  readonly position: string;
  readonly font: string;
  /** `#RRGGBB`, or null for the style's own accent. */
  readonly accent: string | null;
  readonly headline: string;
  /** Seconds into the clip the cover's frame is taken from, or null before one is picked. */
  readonly coverAt: number | null;
  readonly coverPlacement: Placement;
  /** Whether the export puts the cover in as the first frame. */
  readonly bake: boolean;
}

export interface Project {
  readonly version: typeof PROJECT_VERSION;
  /** The file's name when it was last opened: for the operator's eyes only. */
  readonly name: string;
  readonly size: number;
  readonly timings: readonly Timing[] | null;
  /** The operator's corrected text; null until they edit it. */
  readonly transcript: string | null;
  readonly choices: Choices;
  /** ISO 8601, set by the server when it stores the document. */
  readonly updatedAt: string;
}

/** A document refused, and why, in a sentence an operator can act on. */
export interface ProjectRefusal {
  readonly kind: "refused";
  readonly message: string;
}

/** What the server lists: enough to find a clip again without loading it. */
export interface ProjectSummary {
  readonly fingerprint: string;
  readonly name: string;
  readonly size: number;
  readonly words: number;
  readonly updatedAt: string;
}

export const DEFAULT_CHOICES: Choices = {
  captionDesign: null,
  style: "slab",
  position: "safe",
  font: DEFAULT_FONT,
  accent: null,
  headline: "",
  coverAt: null,
  coverPlacement: "low",
  bake: false,
};

export function newProject(name: string, size: number): Project {
  return {
    version: PROJECT_VERSION,
    name,
    size,
    timings: null,
    transcript: null,
    choices: DEFAULT_CHOICES,
    updatedAt: "",
  };
}

export function summarise(fingerprint: string, project: Project): ProjectSummary {
  return {
    fingerprint,
    name: project.name,
    size: project.size,
    words: project.timings?.length ?? 0,
    updatedAt: project.updatedAt,
  };
}

class Refused extends Error {}

const refuse = (message: string): never => {
  throw new Refused(message);
};

function record(value: unknown, where: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) refuse(`${where} is not an object`);
  return value as Record<string, unknown>;
}

function exactKeys(value: Record<string, unknown>, keys: readonly string[], where: string, optional: readonly string[] = []): void {
  for (const key of Object.keys(value)) {
    if (!keys.includes(key)) refuse(`${where} has an unknown field ${repr(key)}`);
  }
  for (const key of keys) {
    if (!(key in value) && !optional.includes(key)) refuse(`${where} has no ${repr(key)}`);
  }
}

function text(value: unknown, where: string, limit: number): string {
  if (typeof value !== "string") refuse(`${where} is not text`);
  const string = value as string;
  if (string.length > limit) refuse(`${where} is ${string.length} characters, more than ${limit}`);
  return string;
}

function whole(value: unknown, where: string): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) {
    refuse(`${where} is not a whole number of zero or more: ${repr(value)}`);
  }
  return value as number;
}

function oneOf<T extends string>(value: unknown, options: readonly T[], where: string): T {
  if (typeof value !== "string" || !(options as readonly string[]).includes(value)) {
    refuse(`${where} is ${repr(value)}; choose one of ${options.join(", ")}`);
  }
  return value as T;
}

function timings(value: unknown): Timing[] | null {
  if (value === null) return null;
  if (!Array.isArray(value)) return refuse("timings is neither a list nor null");
  if (value.length > MAX_WORDS) refuse(`timings has ${value.length} words, more than ${MAX_WORDS}`);
  return value.map((entry: unknown, index): Timing => {
    const where = `word ${index + 1}`;
    const item = record(entry, where);
    exactKeys(item, ["text", "start", "end"], where);
    const word = text(item.text, `${where}'s text`, MAX_WORD);
    if (word.trim() === "") refuse(`${where} has no text`);
    const start = whole(item.start, `${where}'s start`);
    const end = whole(item.end, `${where}'s end`);
    if (end < start) refuse(`${where} (${repr(word)}) ends before it starts: ${end} < ${start}`);
    return { text: word, start, end };
  });
}

function choices(value: unknown): Choices {
  const item = record(value, "choices");
  exactKeys(item, Object.keys(DEFAULT_CHOICES), "choices", ["captionDesign"]);
  const captionDesign = item.captionDesign == null ? null : oneOf(item.captionDesign, CAPTION_DESIGN_NAMES, "the caption design");
  const accent = item.accent;
  if (accent !== null && (typeof accent !== "string" || !/^#[0-9A-Fa-f]{6}$/.test(accent))) {
    refuse(`the accent is ${repr(accent)}; it must look like #568FFF`);
  }
  const coverAt = item.coverAt;
  if (coverAt !== null && (typeof coverAt !== "number" || !Number.isFinite(coverAt) || coverAt < 0)) {
    refuse(`the cover frame's time is ${repr(coverAt)}; it must be seconds, zero or more`);
  }
  if (typeof item.bake !== "boolean") refuse(`bake is ${repr(item.bake)}; it must be true or false`);
  return {
    captionDesign,
    style: oneOf(item.style, Object.keys(STYLES).sort(), "the style"),
    position: oneOf(item.position, Object.keys(POSITIONS).sort(), "the position"),
    font: oneOf(item.font, Object.keys(FONTS).sort(), "the font"),
    accent: accent as string | null,
    headline: text(item.headline, "the headline", MAX_HEADLINE),
    coverAt: coverAt as number | null,
    coverPlacement: oneOf(item.coverPlacement, PLACEMENTS, "the cover placement"),
    bake: item.bake as boolean,
  };
}

/** A project document, checked field by field; anything unexpected is refused, not ignored. */
export function parseProject(payload: unknown): Project | ProjectRefusal {
  try {
    const item = record(payload, "the project");
    exactKeys(item, ["version", "name", "size", "timings", "transcript", "choices", "updatedAt"], "the project");
    if (item.version !== PROJECT_VERSION) {
      refuse(`the project is version ${repr(item.version)}; this subty reads version ${PROJECT_VERSION}`);
    }
    const name = text(item.name, "the name", MAX_NAME);
    if (name === "") refuse("the project has no name");
    return {
      version: PROJECT_VERSION,
      name,
      size: whole(item.size, "the size"),
      timings: timings(item.timings),
      transcript: item.transcript === null ? null : text(item.transcript, "the transcript", MAX_TRANSCRIPT),
      choices: choices(item.choices),
      updatedAt: text(item.updatedAt, "updatedAt", 64),
    };
  } catch (error) {
    if (error instanceof Refused) return { kind: "refused", message: error.message };
    throw error;
  }
}

export function isRefusal(value: Project | ProjectRefusal): value is ProjectRefusal {
  return "kind" in value;
}


/** Apply a whole channel identity while keeping transcript and cover decisions. */
export function applyCaptionDesign(choices: Choices, name: CaptionDesignName): Choices {
  const design = CAPTION_DESIGNS[name];
  return { ...choices, captionDesign: name, style: design.style, position: design.position,
    font: design.font, accent: design.accent };
}
